// "Install for players" across a venue's PCs (F-CAT-09, ADR-0045; spec/13 §9.1), from the one matrix read
// (`GET …/library/installs`): for one library app, how many of the PCs it is offered to have it in the account players
// use, are installing it, were asked to install it and wait for the PC to be free (ADR-0056), could not, have it only in
// another Windows account, miss it from a diskless image, or do not have the Store app at all. Pure, so the card's lines
// are unit-tested without a server.

import type { LibraryInstallMatrix, StationLibraryTitleState, TitleProvisioning } from '@clavian/api-client';

export interface PlayersSummary {
  /** PCs the title is offered to (its classes) that have reported. */
  readonly reported: number;
  /** Of those, the ones that have it installed for players. */
  readonly installed: number;
  readonly installing: number;
  /** Asked to install it on demand (ADR-0056), waiting for the PC to be free. */
  readonly queued: number;
  readonly failed: number;
  readonly otherAccount: number;
  readonly missingFromImage: number;
  readonly notOnPc: number;
}

/** The summary of one entry across the venue's reported PCs. */
export function playersSummary(matrix: LibraryInstallMatrix, entryId: string): PlayersSummary {
  const title = matrix.titles.find((candidate) => candidate.entryId === entryId);
  const counts = { reported: 0, installed: 0, installing: 0, queued: 0, failed: 0, otherAccount: 0, missingFromImage: 0, notOnPc: 0 };
  if (title === undefined) return counts;
  const installed = new Set(matrix.cells.filter((cell) => cell.entryId === entryId && !cell.unverified).map((cell) => cell.stationId));
  const states = new Map(matrix.provisioning.filter((cell) => cell.entryId === entryId).map((cell) => [cell.stationId, cell.state]));
  for (const station of matrix.stations) {
    if (title.classIds.length > 0 && !title.classIds.includes(station.classId)) continue;
    if (station.scannedAt === null) continue;
    counts.reported += 1;
    const state = states.get(station.stationId);
    if (state === 'installed' || (state === undefined && installed.has(station.stationId))) counts.installed += 1;
    else if (state === 'installing') counts.installing += 1;
    else if (state === 'queued') counts.queued += 1;
    else if (state === 'failed') counts.failed += 1;
    else if (state === 'other_account_only') counts.otherAccount += 1;
    else if (state === 'missing_from_image') counts.missingFromImage += 1;
    else if (state === 'not_on_this_pc') counts.notOnPc += 1;
  }
  return counts;
}

/** One line under the switch, with its copy key, the count it reads, its tone and an optional next step. */
export interface PlayersLine {
  readonly key: string;
  readonly count: number;
  readonly tone: 'neutral' | 'warn' | 'danger';
  readonly action?: string;
}

/**
 * What the card says about the app across the PCs, most useful first. With Install for players on: how many have it,
 * then what is in the way (installing, could not, a diskless image, the Store app nowhere). Off: only that some PCs have
 * it for another Windows account, which players cannot open — the switch is what fixes that. Either way, how many PCs
 * staff asked to install it that wait for the PC to be free (ADR-0056).
 */
export function playersLines(summary: PlayersSummary, on: boolean): PlayersLine[] {
  if (summary.reported === 0) return [{ key: 'library.players.notReported', count: 0, tone: 'neutral' }];
  const lines: PlayersLine[] = [];
  if (on) {
    lines.push({ key: 'library.players.installed', count: summary.installed, tone: 'neutral' });
    if (summary.installing > 0) lines.push({ key: 'library.players.installing', count: summary.installing, tone: 'neutral' });
  }
  if (summary.queued > 0) lines.push({ key: 'library.players.queued', count: summary.queued, tone: 'neutral' });
  if (on) {
    if (summary.failed > 0) lines.push({ key: 'library.players.failedCount', count: summary.failed, tone: 'danger' });
    if (summary.missingFromImage > 0) {
      lines.push({ key: 'library.players.missingFromImage', count: summary.missingFromImage, tone: 'warn', action: 'library.players.missingFromImageAction' });
    }
    if (summary.notOnPc > 0) lines.push({ key: 'library.players.notOnPc', count: summary.notOnPc, tone: 'warn', action: 'library.players.notOnPcAction' });
  }
  if (summary.otherAccount > 0) {
    lines.push({ key: 'library.players.otherAccount', count: summary.otherAccount, tone: 'warn', ...(on ? {} : { action: 'library.players.otherAccountAction' }) });
  }
  return lines;
}

/**
 * The one sentence under the entry dialog's PC list, only when it applies: what to do about the most common thing in the way.
 * Returns the copy key, or null when the list says it all. Pure.
 */
export function playersNote(summary: PlayersSummary, on: boolean): string | null {
  if (summary.reported === 0) return null;
  if (!on) return summary.otherAccount > 0 ? 'library.players.otherAccountAction' : null;
  if (summary.missingFromImage > 0) return 'library.players.missingFromImageAction';
  if (summary.notOnPc > 0) return 'library.players.notOnPcAction';
  return null;
}

/** Whether a station's provisioning state is worth a line of its own on the station sheet (anything but installed). */
export function needsAttention(provisioning: TitleProvisioning | null): provisioning is TitleProvisioning {
  return provisioning !== null && provisioning.state !== 'installed';
}

/**
 * The on-demand install a station window offers for one of its titles (ADR-0056): `install` for an app with a recipe that
 * is missing for players (or installed for another Windows account only), `retry` for one whose install failed, and none
 * for an app the PC has, is installing, already waits to install, or cannot install (a diskless client, a Store app on
 * no account of the PC). Pure.
 */
export function installActionOf(title: Pick<StationLibraryTitleState, 'provisionable' | 'installed' | 'unverified' | 'provisioning'>): 'install' | 'retry' | null {
  if (!title.provisionable) return null;
  switch (title.provisioning?.state) {
    case 'failed':
      return 'retry';
    case 'other_account_only':
      return 'install';
    case undefined:
      // Nothing reported for it: installed as detection found it, else missing.
      return title.installed && !title.unverified ? null : 'install';
    default:
      return null;
  }
}

/** The one status line on a library card for an app that can be installed for players (spec/13 §9.1). */
export interface PlayersStatus {
  readonly key: 'off' | 'notReported' | 'all' | 'some' | 'queued' | 'failed';
  readonly tone: 'muted' | 'ok' | 'warn' | 'danger';
  /** PCs that have it for players. */
  readonly installed: number;
  /** PCs the app is offered to that have reported; for "failed", the number of PCs that failed. */
  readonly total: number;
}

/**
 * Where the app stands, in one line: off, not reported yet, on every PC, on some of them (installing and the other states in
 * the way count as "some"), or failed on at least one (the loudest wins).
 */
export function playersStatus(summary: PlayersSummary, on: boolean): PlayersStatus {
  if (!on) return { key: 'off', tone: 'muted', installed: summary.installed, total: summary.reported };
  if (summary.reported === 0) return { key: 'notReported', tone: 'muted', installed: 0, total: 0 };
  if (summary.failed > 0) return { key: 'failed', tone: 'danger', installed: summary.installed, total: summary.failed };
  if (summary.installed === summary.reported) return { key: 'all', tone: 'ok', installed: summary.installed, total: summary.reported };
  // Asked to install and waiting for the PC to be free (ADR-0056): a calm line of its own, ahead of the generic partial one.
  if (summary.queued > 0) return { key: 'queued', tone: 'muted', installed: summary.installed, total: summary.queued };
  return { key: 'some', tone: 'warn', installed: summary.installed, total: summary.reported };
}

/** One PC in the entry's edit dialog: its state for this app. */
export interface PlayersStation {
  readonly stationId: string;
  readonly number: number;
  readonly name: string | null;
  readonly state: 'installed' | 'installing' | 'queued' | 'failed' | 'other_account_only' | 'missing_from_image' | 'not_on_this_pc';
  /**
   * The on-demand install the PC's row offers (ADR-0056, same rules as the station window): Install for a PC that has
   * not reported the app or has it for another Windows account only, Retry for a failed install, else none.
   */
  readonly action: 'install' | 'retry' | null;
}

/** The PCs that have reported and that the entry is offered to, each with where the app stands on it (same rules as the summary). */
export function playersStations(matrix: LibraryInstallMatrix, entryId: string): PlayersStation[] {
  const title = matrix.titles.find((candidate) => candidate.entryId === entryId);
  if (title === undefined) return [];
  const installed = new Set(matrix.cells.filter((cell) => cell.entryId === entryId && !cell.unverified).map((cell) => cell.stationId));
  const states = new Map(matrix.provisioning.filter((cell) => cell.entryId === entryId).map((cell) => [cell.stationId, cell.state]));
  const result: PlayersStation[] = [];
  for (const station of matrix.stations) {
    if (title.classIds.length > 0 && !title.classIds.includes(station.classId)) continue;
    if (station.scannedAt === null) continue;
    const said = states.get(station.stationId);
    const state: PlayersStation['state'] = said ?? (installed.has(station.stationId) ? 'installed' : 'not_on_this_pc');
    const action: PlayersStation['action'] = state === 'failed' ? 'retry' : state === 'other_account_only' || (said === undefined && state === 'not_on_this_pc') ? 'install' : null;
    result.push({ stationId: station.stationId, number: station.number, name: station.name, state, action });
  }
  return result;
}
